import { type StateBuilder } from '@atlassian/state-machine/builder/types';
import { keep } from '@atlassian/state-machine/common/context';
import { always } from '@atlassian/state-machine/common/guards';

import { asRejectEvent, asyncState } from '../../async-state.ts';
import {
  generateTraceId,
  type SocketMachineContext,
} from '../../network-state-machine-context.ts';
import { type Events, type EventsLookup } from '../../network-state-machine-events.ts';
import { type State } from '../../network-state-machine.ts';
import {
  connectingState,
  connectingStates,
  type ConnectingStates,
} from '../connecting/connecting-state.ts';

import { onExitActive } from './active-effects.ts';
import {
  isPossibleToRetry,
  isSessionRevoked,
  resetDebounce,
} from './active-state-context.ts';
import { closedAsync } from './closed/closed-effects.ts';
import {
  onEnterConnected,
  onReEnterConnected,
  onExitConnected,
} from './connected/connected-effects.ts';

export const activeStates = [
  'active',
  'connecting',
  'connected',
  'reconnecting',
  ...connectingStates.map((s) => `connecting:${s}` as `connecting:${ConnectingStates}`),
  'closed',
] as const;
export type ActiveStates = (typeof activeStates)[number];

export function activeState(
  builder: StateBuilder<State, Events, EventsLookup, SocketMachineContext>,
): StateBuilder<State, Events, EventsLookup, SocketMachineContext> {
  return builder
    .state('connecting', connectingState)
    .state('connected', (s) =>
      s
        .when('pause', (t) => t.if(always).context(keep).to('connecting:paused'))
        .when('close', (t) => [
          t.if(isSessionRevoked).context(resetDebounce).to('closed'),
          t.if(isPossibleToRetry).context(keep).to('closed'),
          t.if(always).context(keep).to('destroyed'),
        ])
        .when('error', (t) => t.if(always).context(keep).to('closed'))
        .when('restart', (t) => t.if(always).context(keep).to('closed'))
        .when('send', (t) => t.if(always).context(keep).to('connected'))
        .on('enter', onEnterConnected)
        .on('re-enter', onReEnterConnected)
        .on('exit', onExitConnected),
    )
    .state(
      'closed',
      asyncState(closedAsync, asRejectEvent, (s) =>
        s.when('reconnect', (t) =>
          t.if(always).context(generateTraceId).to('connecting:waiting'),
        ),
      ),
    )
    .when('abort', (t) => t.if(always).context(keep).to('destroyed'))
    .on('exit', onExitActive);
}
